import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, 
  Phone, 
  Calendar, 
  Users, 
  Award, 
  Heart,
  Stethoscope,
  Baby,
  Scissors,
  Siren,
  ChevronRight,
  Star,
  Quote,
  Shield,
  MapPin,
  CheckCircle,
  Activity,
  Target,
  Eye,
  Ear
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Stethoscope: Stethoscope,
  Baby: Baby,
  Heart: Heart,
  Scissors: Scissors,
  Siren: Siren,
};

// Icon mapping for doctor specializations
const getDoctorIcon = (iconType) => {
  const iconClass = "w-12 h-12 text-primary";
  
  switch (iconType) {
    case 'dental':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C7.5 2 6 5 6 8c0 2 .5 3 1 4.5.5 1.5 1 3 1 5.5 0 2 1 4 2.5 4s2-1.5 2.5-3.5c.5 2 1 3.5 2.5 3.5s2.5-2 2.5-4c0-2.5.5-4 1-5.5S18 10 18 8c0-3-1.5-6-6-6z"/>
        </svg>
      );
    case 'eye':
      return <Eye className={iconClass} />;
    case 'maternal':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="5"/>
          <path d="M12 13c-4 0-7 2-7 5v2h14v-2c0-3-3-5-7-5z"/>
          <circle cx="12" cy="8" r="2"/>
        </svg>
      );
    case 'pediatric':
      return <Baby className={iconClass} />;
    case 'physiotherapy':
      return <Activity className={iconClass} />;
    case 'ent':
      return <Ear className={iconClass} />;
    case 'stethoscope':
    default:
      return <Stethoscope className={iconClass} />;
  }
};

export default function Home() {
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First seed the database
        await axios.post(`${API}/seed`).catch(() => {});
        
        const [deptRes, docRes, newsRes] = await Promise.all([
          axios.get(`${API}/departments`),
          axios.get(`${API}/doctors`),
          axios.get(`${API}/news?per_page=3`)
        ]);
        
        setDepartments(deptRes.data.slice(0, 6));
        setDoctors(docRes.data.slice(0, 4));
        setNews(newsRes.data.articles || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const stats = [
    { icon: Users, value: '15,000+', label: 'Patients Served' },
    { icon: Award, value: '2+', label: 'Years of Service' },
    { icon: Stethoscope, value: '40+', label: 'Medical Experts' },
    { icon: Heart, value: '98%', label: 'Patient Satisfaction' },
  ];

  const trustElements = [
    { icon: Award, title: 'Quality Care', description: 'Highest standards of medical excellence' },
    { icon: Heart, title: 'Compassionate Service', description: 'Patient-centered approach' },
    { icon: MapPin, title: 'Accessible Healthcare', description: 'Serving Mapinga & surrounding areas' },
    { icon: Users, title: 'Professional Medical Team', description: 'Experienced healthcare professionals' },
  ];

  const testimonials = [
    {
      name: 'Maria Josephat',
      role: 'Patient',
      content: 'The care I received at BAOBAB Hospital was exceptional. The staff were compassionate and the facilities were top-notch.',
      rating: 5,
    },
    {
      name: 'John Mwamba',
      role: 'Patient Family',
      content: 'My mother received excellent treatment here. The doctors took time to explain everything and made us feel at ease.',
      rating: 5,
    },
    {
      name: 'Grace Mkumbo',
      role: 'Patient',
      content: 'From the moment I walked in, I felt welcomed. The maternity ward staff were incredibly supportive during my delivery.',
      rating: 5,
    },
  ];

  return (
    <div>
      {/* Hero Section with Transparent Header */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/ae4wsgyo_P13.jpeg"
            alt="Baobab Hospital Building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-primary/70 to-primary/60" />
        </div>
        
        {/* Hospital Branding - Above Navigation */}
        <div className="absolute top-6 left-0 right-0 z-20">
          <div className="container-custom">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/5oi0txpb_Logooooo%20BH%20.png" 
                alt="Baobab Hospital Logo" 
                className="w-16 h-16 md:w-20 md:h-20 object-contain"
              />
              <div className="text-center">
                <h1 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide" style={{ fontFamily: "'Cinzel', serif" }}>
                  BAOBAB HOSPITAL
                </h1>
                <p className="text-white/90 text-xs md:text-sm tracking-[0.25em] uppercase mt-1" style={{ fontFamily: "'Marcellus', serif" }}>
                  Your Life is in Safe Hands
                </p>
              </div>
            </div>
            
            {/* Navigation Menu */}
            <nav className="flex items-center justify-center">
              <div className="flex items-center justify-center space-x-1 md:space-x-2 flex-wrap">
                <Link to="/" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  Home
                </Link>
                <Link to="/about/background" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  About
                </Link>
                <Link to="/departments" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  Departments
                </Link>
                <Link to="/executive-health" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  Executive Health
                </Link>
                <Link to="/doctors" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  Our Doctors
                </Link>
                <Link to="/find-doctor" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  Find a Doctor
                </Link>
                <Link to="/news" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  News
                </Link>
                <Link to="/contact" className="px-3 py-2 text-white hover:text-white/80 font-medium transition-colors whitespace-nowrap text-sm">
                  Contact
                </Link>
                <Link to="/book-appointment" className="ml-2">
                  <Button className="bg-white text-primary hover:bg-white/90 rounded-lg px-4 py-2 font-medium text-sm">
                    <Calendar className="w-4 h-4 mr-1" />
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        </div>

        <div className="container-custom relative z-10 pt-48 md:pt-56">
          <div className="max-w-2xl text-white">
            <p className="text-accent-light font-semibold text-lg mb-4 animate-fade-in">Welcome to Baobab Hospital</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6 animate-fade-in stagger-1 leading-tight text-left">
              Your Trusted Healthcare Partner in Pwani
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed animate-fade-in stagger-2 font-body text-justify">
              Baobab Hospital is committed to delivering quality, compassionate, and accessible healthcare services to the Mapinga community and neighboring areas in Pwani. We provide patient-centered medical care in a safe, healing, and professional environment.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in stagger-3">
              <Link to="/book-appointment" data-testid="home-hero-book-btn">
                <Button className="bg-white hover:bg-slate-100 text-primary rounded-lg px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                  <Calendar className="mr-2 w-5 h-5" />
                  Book an Appointment
                </Button>
              </Link>
              <Link to="/find-doctor" data-testid="home-hero-find-doctor-btn">
                <Button variant="outline" className="bg-white/10 border-2 border-white text-white hover:bg-white hover:text-primary rounded-lg px-8 py-6 text-lg font-semibold backdrop-blur-sm transition-all hover:-translate-y-0.5">
                  <Stethoscope className="mr-2 w-5 h-5" />
                  Find a Doctor
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 animate-fade-in stagger-4">
              <Link to="/departments" data-testid="home-hero-departments-btn">
                <Button variant="outline" className="bg-white/5 border border-white/30 text-white hover:bg-white/20 rounded-lg px-6 py-4 font-medium backdrop-blur-sm transition-all">
                  <Activity className="mr-2 w-4 h-4" />
                  Explore Departments
                </Button>
              </Link>
              <Link to="/contact" data-testid="home-hero-contact-btn">
                <Button variant="outline" className="bg-white/5 border border-white/30 text-white hover:bg-white/20 rounded-lg px-6 py-4 font-medium backdrop-blur-sm transition-all">
                  <Phone className="mr-2 w-4 h-4" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Emergency Card */}
        <div className="absolute bottom-8 right-8 hidden lg:block animate-fade-in stagger-5">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 max-w-xs shadow-float">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-red-500 rounded-lg flex items-center justify-center shadow-md animate-pulse">
                <Phone className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium font-body">24/7 Emergency Line</p>
                <p className="font-heading font-bold text-primary text-lg">+255 659 984 488</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 font-body">Our emergency team is available around the clock for urgent medical needs.</p>
          </div>
        </div>
      </section>

      {/* Quick Access Section - Strong CTAs */}
      <section className="py-6 bg-white border-b border-slate-100 shadow-sm" data-testid="home-quick-access">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/book-appointment" className="group" data-testid="quick-book">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-r from-primary-50 to-primary-50/50 hover:from-primary hover:to-primary-700 transition-all duration-300 group-hover:shadow-lg">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-primary group-hover:text-white transition-colors">Book Appointment</h3>
                  <p className="text-xs text-slate-500 group-hover:text-white/80 transition-colors font-body">Schedule your visit</p>
                </div>
              </div>
            </Link>
            <Link to="/find-doctor" className="group" data-testid="quick-find-doctor">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-r from-accent-light to-accent-light/50 hover:from-accent hover:to-accent-soft transition-all duration-300 group-hover:shadow-lg">
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-primary group-hover:text-white transition-colors">Find a Doctor</h3>
                  <p className="text-xs text-slate-500 group-hover:text-white/80 transition-colors font-body">Search specialists</p>
                </div>
              </div>
            </Link>
            <Link to="/departments" className="group" data-testid="quick-departments">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-r from-primary-100 to-primary-50 hover:from-primary hover:to-primary-700 transition-all duration-300 group-hover:shadow-lg">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-primary group-hover:text-white transition-colors">Explore Departments</h3>
                  <p className="text-xs text-slate-500 group-hover:text-white/80 transition-colors font-body">View our services</p>
                </div>
              </div>
            </Link>
            <Link to="/contact" className="group" data-testid="quick-contact">
              <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-r from-slate-100 to-slate-50 hover:from-primary hover:to-primary-700 transition-all duration-300 group-hover:shadow-lg">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-primary group-hover:text-white transition-colors">Contact Us</h3>
                  <p className="text-xs text-slate-500 group-hover:text-white/80 transition-colors font-body">Get in touch</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Elements Section */}
      <section className="py-12 bg-slate-50 border-b border-slate-200" data-testid="home-trust">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {trustElements.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4">
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-primary text-sm md:text-base">{item.title}</h3>
                  <p className="text-slate-600 text-xs md:text-sm font-body">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-primary-700" data-testid="home-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-accent-light" />
                </div>
                <p className="font-heading text-3xl md:text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-white/80 font-body">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services/Departments Section */}
      <section className="section-spacing bg-white" data-testid="home-services">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Our Services</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Comprehensive Healthcare Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto font-body">
              We offer a wide range of medical services to meet the healthcare needs of the Mapinga community and surrounding areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              Array(6).fill(0).map((_, i) => (
                <div key={i} className="card-service animate-pulse">
                  <div className="w-14 h-14 bg-slate-200 rounded-lg mb-4" />
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-5/6" />
                </div>
              ))
            ) : (
              departments.map((dept, index) => {
                const IconComponent = iconMap[dept.icon] || Stethoscope;
                return (
                  <Card key={dept.id} className="card-service group" data-testid={`service-card-${index}`}>
                    <CardContent className="p-8">
                      <div className="w-14 h-14 bg-primary-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary transition-colors">
                        <IconComponent className="w-7 h-7 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-primary mb-3">{dept.name}</h3>
                      <p className="text-slate-600 mb-4 line-clamp-2 font-body">{dept.description}</p>
                      <Link to="/departments" className="inline-flex items-center text-accent hover:text-primary font-medium transition-colors">
                        Learn More <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/departments" data-testid="home-view-all-services">
              <Button className="btn-secondary">
                View All Departments
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-spacing bg-slate-50" data-testid="home-about">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              {/* Image Gallery Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <img
                    src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/53pkoyid_P14.jpeg"
                    alt="Baobab Hospital Laboratory"
                    className="rounded-xl shadow-float w-full h-64 object-cover"
                  />
                </div>
                <img
                  src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/6jm8b09k_P11.jpeg"
                  alt="Baobab Hospital Building"
                  className="rounded-xl shadow-card w-full h-40 object-cover"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/u6f3cdcd_P15.jpeg"
                  alt="Baobab Hospital Main Entrance"
                  className="rounded-xl shadow-card w-full h-40 object-cover"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/cn42uzd7_AMBULANCE%20.jpeg"
                  alt="Baobab Hospital Ambulance"
                  className="rounded-xl shadow-card w-full h-40 object-cover"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/n4bcwcr4_P3.jpeg"
                  alt="Baobab Hospital Campus"
                  className="rounded-xl shadow-card w-full h-40 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-white rounded-xl p-6 hidden md:block shadow-lg z-10">
                <p className="font-heading text-4xl font-bold">20+</p>
                <p className="text-white/90 font-body">Years of Excellence</p>
              </div>
            </div>
            <div>
              <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">About Baobab Hospital</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6 text-left">
                Your Trusted Healthcare Partner in Pwani
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed font-body text-justify">
                Baobab Hospital is a healthcare institution dedicated to serving Mapinga, Bagamoyo, Pwani, and nearby communities. We are committed to providing quality healthcare, promoting community welfare, delivering compassionate treatment, and ensuring modern service delivery.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed font-body text-justify">
                With a team of dedicated healthcare professionals, modern facilities, and a patient-centered approach, we strive to deliver the best possible care in a safe, healing, and professional environment.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                    <Award className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-slate-700 font-body">Certified Doctors</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-slate-700 font-body">Compassionate Care</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-slate-700 font-body">Safe Environment</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                    <Activity className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-slate-700 font-body">Modern Equipment</span>
                </div>
              </div>
              <Link to="/about/background" data-testid="home-learn-more-btn">
                <Button className="btn-primary">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="section-spacing bg-gradient-to-br from-primary via-primary-700 to-primary-800" data-testid="home-vision-mission">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent-light font-semibold mb-2 tracking-wide uppercase text-sm">Our Purpose</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Vision & Mission
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto font-body">
              Guiding principles that drive our commitment to exceptional healthcare
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Mission Card */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden group hover:bg-white/15 transition-all duration-500">
              <CardContent className="p-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <span className="text-accent-light text-sm font-medium uppercase tracking-wider">Our</span>
                    <h3 className="font-heading text-3xl font-bold text-white">Mission</h3>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-accent to-accent/30 rounded-full" />
                  <p className="text-white/90 leading-relaxed text-lg pl-6 font-body text-justify">
                    To provide quality, compassionate and accessible healthcare services to Mapinga community and neighboring Pwani areas, ensuring a positive and healing environment that prioritizes health, well-being and community welfare.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Vision Card */}
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden group hover:bg-white/15 transition-all duration-500">
              <CardContent className="p-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <span className="text-accent-light text-sm font-medium uppercase tracking-wider">Our</span>
                    <h3 className="font-heading text-3xl font-bold text-white">Vision</h3>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-accent to-accent/30 rounded-full" />
                  <p className="text-white/90 leading-relaxed text-lg pl-6 font-body text-justify">
                    To become a leading healthcare provider in the Mapinga and Pwani areas, known for delivering exceptional medical services, fostering a patient approach and contributing to the overall well being of the community we serve.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Link to="/about/background" data-testid="home-view-values">
              <Button className="bg-white/10 border-2 border-white text-white hover:bg-white hover:text-primary rounded-lg px-8 py-4 font-semibold backdrop-blur-sm">
                View Our Core Values
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Doctors Section */}
      <section className="section-spacing bg-white" data-testid="home-doctors">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold mb-2 tracking-wide uppercase text-sm">Our Medical Team</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Meet Our Expert Doctors
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto font-body">
              Our team of experienced and dedicated healthcare professionals is here to provide you with the best medical care.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              Array(4).fill(0).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-white rounded-2xl p-6 shadow-sm border">
                    <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4" />
                    <div className="h-4 bg-slate-200 rounded w-3/4 mx-auto mb-2" />
                    <div className="h-5 bg-slate-200 rounded w-2/3 mx-auto" />
                  </div>
                </div>
              ))
            ) : (
              doctors.slice(0, 8).map((doctor, index) => (
                <Link to={`/doctors/${doctor.id}`} key={doctor.id} data-testid={`doctor-card-${index}`}>
                  <Card className="bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-slate-100 group h-full">
                    <CardContent className="p-6 text-center">
                      {/* Icon or Photo */}
                      <div className="mb-4">
                        {doctor.image ? (
                          <div className="w-16 h-16 mx-auto rounded-full overflow-hidden border-4 border-primary/10">
                            <img
                              src={doctor.image}
                              alt={doctor.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center group-hover:from-primary/20 group-hover:to-primary/10 transition-colors">
                            {getDoctorIcon(doctor.icon_type)}
                          </div>
                        )}
                      </div>
                      
                      {/* Qualification Badge */}
                      <span className="inline-block px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded mb-2">
                        {doctor.specialty_code || doctor.title || 'Medical Professional'}
                      </span>
                      
                      {/* Doctor Name */}
                      <h3 className="font-heading font-semibold text-slate-800 text-base group-hover:text-primary transition-colors">
                        {doctor.name}
                      </h3>
                      
                      {/* Specialty */}
                      <p className="text-slate-500 text-sm mt-1 font-body">
                        {doctor.specialty || doctor.title}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/doctors" data-testid="home-view-all-doctors">
              <Button className="btn-secondary">
                View All Doctors
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-spacing bg-gradient-to-r from-primary to-primary-700" data-testid="home-testimonials">
        <div className="container-custom">
          <div className="mb-12">
            <p className="text-accent-light font-semibold mb-2 tracking-wide uppercase text-sm">Testimonials</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4 text-left">
              What Our Patients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20" data-testid={`testimonial-${index}`}>
                <CardContent className="p-8 flex flex-col">
                  <Quote className="w-10 h-10 text-accent-light mb-4" />
                  <p className="text-white/90 mb-6 leading-relaxed font-body text-left">{testimonial.content}</p>
                  <div className="flex justify-center gap-1 mb-4">
                    {Array(testimonial.rating).fill(0).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-accent-light text-accent-light" />
                    ))}
                  </div>
                  <div className="flex flex-col items-center">
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-white/70 text-sm font-body">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="section-spacing bg-slate-50" data-testid="home-news">
        <div className="container-custom">
          <div className="mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Latest Updates</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4 text-left">
              News & Articles
            </h2>
            <p className="text-slate-600 max-w-2xl font-body text-justify">
              Stay informed about our latest news, health tips, and community initiatives.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {loading ? (
              Array(3).fill(0).map((_, i) => (
                <div key={i} className="card-service animate-pulse">
                  <div className="aspect-video bg-slate-200 rounded-t-xl" />
                  <div className="p-6">
                    <div className="h-4 bg-slate-200 rounded w-1/4 mb-3" />
                    <div className="h-6 bg-slate-200 rounded w-full mb-3" />
                    <div className="h-4 bg-slate-200 rounded w-5/6" />
                  </div>
                </div>
              ))
            ) : (
              news.map((article, index) => (
                <Card key={article.id} className="card-service overflow-hidden" data-testid={`news-card-${index}`}>
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={article.image || 'https://images.unsplash.com/photo-1579165466741-7f35e4755660?w=800'}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6">
                    <span className="text-xs font-medium text-accent uppercase tracking-wider">{article.category}</span>
                    <h3 className="font-heading font-semibold text-primary text-lg mt-2 mb-3 line-clamp-2 text-left">{article.title}</h3>
                    <p className="text-slate-600 text-sm line-clamp-2 mb-4 font-body">{article.excerpt}</p>
                    <Link to={`/news/${article.slug}`} className="inline-flex items-center text-primary hover:text-accent font-medium transition-colors">
                      Read More <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/news" data-testid="home-view-all-news">
              <Button className="btn-secondary">
                View All News
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent to-accent-soft" data-testid="home-cta">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white text-center md:text-left">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                Ready to Book Your Appointment?
              </h2>
              <p className="text-white/90 font-body">
                Schedule a visit with our expert doctors today. Quality care is just a click away.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-appointment" data-testid="home-cta-book">
                <Button className="bg-white text-primary hover:bg-slate-100 rounded-lg px-8 py-6 font-semibold shadow-lg">
                  <Calendar className="mr-2 w-5 h-5" />
                  Book Now
                </Button>
              </Link>
              <a href="tel:+255659984488" data-testid="home-cta-call">
                <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary rounded-lg px-8 py-6 font-semibold">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
